import { doc, getDoc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];

// No conversion needed - we use 12-hour format throughout
export function formatTime12Hour(time) {
  return time || '';
}

/// Converts a 12-hour time string (e.g. "8:00 AM", "10:30 AM") into an
/// integer suitable for chronological sorting. String comparison fails
/// here because "10:00 AM" sorts before "8:00 AM" lexicographically.
export function timetableTimeOrder(time12h) {
  if (!time12h) return 0;
  const cleaned = time12h.trim().toUpperCase();
  const isPM = cleaned.includes('PM');
  const isAM = cleaned.includes('AM');
  const withoutPeriod = cleaned.replace(/AM/g, '').replace(/PM/g, '').trim();
  const parts = withoutPeriod.split(':');
  if (parts.length < 1) return 0;
  const hour = parseInt(parts[0], 10);
  if (isNaN(hour)) return 0;
  const minute = parts.length > 1 ? (parseInt(parts[1], 10) || 0) : 0;
  let hour24 = hour;
  if (isPM && hour !== 12) hour24 += 12;
  if (isAM && hour === 12) hour24 = 0;
  return hour24 * 60 + minute;
}

/// Sort classes in a natural academic order (Kindergarten → Grade 1-6 → Forms).
export function schoolClassSortOrder(a, b) {
  return schoolClassSortKey(a.grade || '', a.name || '') < schoolClassSortKey(b.grade || '', b.name || '') ? -1 : 1;
}

export function schoolClassSortKey(grade, name) {
  const g = (grade || '').toLowerCase();
  const n = (name || '').toLowerCase();

  if (g.includes('kind') || g.includes('preschool') || g.includes('nursery') ||
      n.includes('kind') || n.includes('preschool') || n.includes('nursery')) {
    return [0, sectionSuffixIndex(n), n];
  }
  if (g.includes('grade')) {
    const digits = g.replace('grade', '').trim();
    const level = parseInt(digits, 10);
    if (!isNaN(level)) return [level, sectionSuffixIndex(n), n];
  }
  if (n.includes('grade')) {
    const digits = n.replace('grade', '').trim();
    const level = parseInt(digits, 10);
    if (!isNaN(level)) return [level, sectionSuffixIndex(n), n];
  }
  if (g.includes('form') || g.includes('jr h') || g.includes('sr h')) {
    const digits = g.replace(/form|jr h|sr h/g, '').trim();
    const level = parseInt(digits, 10);
    if (!isNaN(level)) return [100 + level, sectionSuffixIndex(n), n];
  }
  if (n.includes('form')) {
    const digits = n.replace('form', '').trim();
    const level = parseInt(digits, 10);
    if (!isNaN(level)) return [100 + level, sectionSuffixIndex(n), n];
  }
  return [1000, 0, n];
}

function sectionSuffixIndex(name) {
  const trimmed = name.trim();
  const parts = trimmed.split(' ');
  if (parts.length > 0) {
    const last = parts[parts.length - 1];
    if (last.length === 1 && /[a-z]/i.test(last)) {
      return last.toUpperCase().charCodeAt(0) - 'A'.charCodeAt(0) + 1;
    }
  }
  return 0;
}

class TimetableService {
  getWeekdays() {
    return [...WEEKDAYS];
  }

  async getTimetable(classId) {
    if (!classId) return { classId: null, entries: [], status: 'Draft' };
    try {
      const docRef = doc(db, 'timetables', classId);
      const snapshot = await getDoc(docRef);
      if (snapshot.exists()) {
        const data = snapshot.data();
        return {
          id: data.id || classId,
          classId: data.classId || classId,
          academicYearId: data.academicYearId || null,
          termId: data.termId || null,
          gradeId: data.gradeId || null,
          status: data.status || 'Draft',
          publishedAt: data.publishedAt || null,
          publishedBy: data.publishedBy || null,
          schoolId: data.schoolId || null,
          entries: Array.isArray(data.entries) ? data.entries : []
        };
      }
    } catch (error) {
      console.error('TimetableService: failed to load timetable', classId, error.message);
    }
    return { classId, entries: [], status: 'Draft' };
  }

  async saveTimetableWithEntries(classId, entries, schoolId = null, status = 'Draft') {
    if (!classId) throw new Error('Class ID is required');
    const payload = {
      id: classId,
      classId,
      entries: entries.map(entry => ({
        id: entry.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        dayOfWeek: entry.dayOfWeek,
        subjectName: entry.subjectName?.trim() || '',
        subjectId: entry.subjectId || null,
        teacherId: entry.teacherId || null,
        gradeId: entry.gradeId || null,
        startTime: entry.startTime?.trim() || '',
        endTime: entry.endTime?.trim() || '',
        room: entry.room?.trim() || '',
        roomId: entry.roomId || null,
        slotIndex: typeof entry.slotIndex === 'number' ? entry.slotIndex : 0,
        entryType: entry.entryType || 'subject'
      })),
      schoolId,
      status,
      updatedAt: serverTimestamp()
    };
    await setDoc(doc(db, 'timetables', classId), payload, { merge: true });
    return payload;
  }

  async saveTimetable(classId, entries, schoolId = null, options = {}) {
    if (!classId) throw new Error('Class ID is required');
    const payload = {
      id: classId,
      classId,
      entries: entries.map(entry => ({
        id: entry.id || `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        dayOfWeek: entry.dayOfWeek,
        subjectName: entry.subjectName?.trim() || '',
        subjectId: entry.subjectId || null,
        teacherId: entry.teacherId || null,
        gradeId: entry.gradeId || null,
        startTime: entry.startTime?.trim() || '',
        endTime: entry.endTime?.trim() || '',
        room: entry.room?.trim() || '',
        roomId: entry.roomId || null,
        slotIndex: typeof entry.slotIndex === 'number' ? entry.slotIndex : 0,
        entryType: entry.entryType || 'subject'
      })),
      schoolId,
      academicYearId: options.academicYearId || null,
      termId: options.termId || null,
      gradeId: options.gradeId || null,
      status: options.status || 'Draft',
      updatedAt: serverTimestamp()
    };
    await setDoc(doc(db, 'timetables', classId), payload, { merge: true });
    return payload;
  }

  async publishTimetable(classId, publishedBy) {
    if (!classId) throw new Error('Class ID is required');
    const timetableRef = doc(db, 'timetables', classId);
    await updateDoc(timetableRef, {
      status: 'Published',
      publishedAt: serverTimestamp(),
      publishedBy,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  }

  async archiveTimetable(classId) {
    if (!classId) throw new Error('Class ID is required');
    const timetableRef = doc(db, 'timetables', classId);
    await updateDoc(timetableRef, {
      status: 'Archived',
      updatedAt: serverTimestamp()
    });
    return { success: true };
  }

  async revertToDraft(classId) {
    if (!classId) throw new Error('Class ID is required');
    const timetableRef = doc(db, 'timetables', classId);
    await updateDoc(timetableRef, {
      status: 'Draft',
      publishedAt: null,
      publishedBy: null,
      updatedAt: serverTimestamp()
    });
    return { success: true };
  }
}

export const timetableService = new TimetableService();
